import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import api from "../../../api/axios";

/* Both halves of /api/bulk are superAdminOnly — allocation decides what every
   store sells, so a store-bound admin has no business there. Matching the
   constant in Store/Stores.jsx and middlewares/adminAuth.js. */
const SUPERADMIN_ROLE = 1;

/* ── Bulk store allocation ────────────────────────────────────────────
   Four steps on one page: pick the items, preview what matched, choose
   the verb, apply. Nothing is written before step 4.

   Store allocation ONLY — this screen never touches stock or price.   */

/* The `operation` values POST /api/bulk/allocate accepts. These strings are
   sent over the wire verbatim and must stay identical to the OPERATIONS list
   in backend/routes/bulkRoutes.js — anything else is rejected with a 400. */
const OPERATIONS = [
  {
    value: "all_stores",
    title: "Make available everywhere",
    hint: "Back to the default: sells in every store, no exceptions.",
    needsStores: false,
  },
  {
    value: "limit_to",
    title: "Limit to these stores",
    hint: "Replaces the list — the product sells ONLY where you pick.",
    needsStores: true,
    /* the catalogue is entirely available-everywhere today, so this verb is
       a narrowing for every product it touches, and the server names each */
    caution:
      "This narrows the products: anything selling in every store today will be cut back to only the stores you pick. Everywhere else stops showing it.",
  },
  {
    value: "add_stores",
    title: "Add stores",
    hint: "Appends to an existing list. A product that already sells everywhere is only put back where a previous hide took it out.",
    needsStores: true,
  },
  {
    value: "remove_stores",
    title: "Remove stores",
    hint: "Takes these stores off the list; the others stay. A product that would be left with no store at all is skipped.",
    needsStores: true,
  },
  {
    value: "hide_in",
    title: "Hide in these stores",
    hint: "Stays on all-stores, but these branches stop showing it.",
    needsStores: true,
  },
  {
    value: "unhide_in",
    title: "Unhide in these stores",
    hint: "Clears the hidden-here exception for these branches.",
    needsStores: true,
  },
];

const STEPS = [
  { n: 1, title: "Select items" },
  { n: 2, title: "Preview" },
  { n: 3, title: "Choose action" },
  { n: 4, title: "Apply" },
];

/* headers the Excel reader understands, in priority order */
const ID_KEYS = [
  "productcode",
  "code",
  "skucode",
  "sku",
  "name",
  "productname",
  "identifier",
  "product",
];

const normKey = (k) => String(k || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/* Red and amber have no semantic token in milliyo-theme.css, and the literals
   the rest of the portal uses (#dc2626 / #b45309) fall to roughly 2.8:1 on the
   dark green surfaces — unreadable. Scoping a light and a dark pair here keeps
   the warning colours theme-aware without touching the shared stylesheet. */
const BULK_CSS = `
.mly-bulk{
  --bulk-danger:#B42318; --bulk-danger-soft:rgba(180,35,24,.09);
  --bulk-warn:#92400E;   --bulk-warn-soft:rgba(146,64,14,.10);
}
html[data-theme="dark"] .mly-bulk{
  --bulk-danger:#FF9A8F; --bulk-danger-soft:rgba(255,154,143,.10);
  --bulk-warn:#F5C36B;   --bulk-warn-soft:rgba(245,195,107,.12);
}
`;

/* ── ERRORS OFF THE NEW SERVICE ──
   The .NET service answers with a REAL HTTP STATUS and a { code, message }
   body. Node's "200 carrying status:400 in the payload" is gone, so nothing
   here may read a status field out of a body — and a bare refusal
   ({ code: "forbidden" }) carries no message at all, which is why the status
   has to supply the sentence when the body cannot. */
const apiError = (e, fallback) => {
  const status = e?.response?.status;
  const message = e?.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message.trim();
  if (!status) return "Could not reach the server.";
  if (status === 401) return "Your session has expired — sign in again.";
  if (status === 403) return "Your account is not allowed to do that.";
  if (status === 404) return "The server has no such record.";
  if (status === 503) return "The server is busy — try again in a moment.";
  return fallback;
};

/* the API may hand back a bare string or a row object — read either */
const lineOf = (x) =>
  typeof x === "string" ? x : String(x?.line ?? x?.identifier ?? x?.value ?? "");

const idOf = (c) => String(c?.productId || c?._id || c?.id || "");

const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  color: "var(--mly-text-muted)",
  marginBottom: 8,
  textTransform: "uppercase",
};

const inputStyle = {
  width: "100%",
  padding: "11px 14px",
  borderRadius: 10,
  border: "1.5px solid var(--mly-border)",
  background: "var(--mly-surface)",
  color: "var(--mly-text)",
  fontSize: 14,
  boxSizing: "border-box",
  outline: "none",
};

const sectionStyle = {
  border: "1.5px solid var(--mly-border)",
  borderRadius: 14,
  background: "var(--mly-surface)",
  padding: "20px 22px",
  marginBottom: 18,
};

const primaryBtn = (disabled) => ({
  padding: "11px 24px",
  borderRadius: 10,
  border: "none",
  background: disabled ? "var(--mly-surface-3)" : "var(--mly-heading)",
  color: disabled ? "var(--mly-text-muted)" : "var(--mly-on-accent)",
  fontWeight: 700,
  fontSize: 14,
  cursor: disabled ? "not-allowed" : "pointer",
});

const ghostBtn = {
  padding: "11px 20px",
  borderRadius: 10,
  border: "1.5px solid var(--mly-border)",
  background: "var(--mly-surface-2)",
  color: "var(--mly-text-soft)",
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
};

const linkBtn = {
  border: "none",
  background: "none",
  padding: 0,
  fontSize: 12.5,
  fontWeight: 700,
  color: "var(--mly-heading)",
  cursor: "pointer",
  textDecoration: "underline",
};

/* "A", "A and B", "A, B and C", "A, B and 3 others" */
const joinNames = (names) => {
  if (names.length === 0) return "the selected stores";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  if (names.length <= 4)
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${names.slice(0, 3).join(", ")} and ${names.length - 3} others`;
};

const BulkAllocate = () => {
  const role = useSelector((state) => state.adminUser.role);
  const canAllocate = Number(role) === SUPERADMIN_ROLE;

  /* ── reference data ── */
  const [stores, setStores] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [refError, setRefError] = useState("");

  /* ── step 1 ── */
  const [tab, setTab] = useState("paste");
  const [pasteText, setPasteText] = useState("");
  const [excelLines, setExcelLines] = useState([]);
  const [excelFileName, setExcelFileName] = useState("");
  const [filter, setFilter] = useState({
    category: "",
    subCategory: "",
    channel: "",
  });
  const fileRef = useRef(null);

  /* ── step 2 ── */
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [choices, setChoices] = useState({}); // ambiguous index -> productId | ""

  /* ── step 3 ── */
  const [operation, setOperation] = useState("");
  const [storeIds, setStoreIds] = useState([]);

  /* ── step 4 ── */
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const [error, setError] = useState("");

  useEffect(() => {
    if (!canAllocate) return undefined;
    let alive = true;
    Promise.all([
      api.get("/admin/stores"),
      api.get("/admin/catalog/subcategories/all"),
    ])
      .then(([storeRes, subRes]) => {
        if (!alive) return;
        /* TWO DIFFERENT SHAPES, deliberately not folded together.
           /admin/stores answers with a BARE ARRAY, so there is no wrapper to
           unwrap; /admin/catalog/subcategories/all answers with a literal
           { data: [...] } object, so `.data.data` is still the array there.
           "Drop one .data everywhere" is right for the first and wrong for
           the second. */
        setStores(Array.isArray(storeRes.data) ? storeRes.data : []);
        setSubCategories(
          Array.isArray(subRes.data?.data) ? subRes.data.data : []
        );
      })
      .catch((e) => {
        if (!alive) return;
        setRefError(apiError(e, "Failed to load stores and categories"));
      });
    return () => {
      alive = false;
    };
  }, [canAllocate]);

  /* categories are derived from the sub-category list, so one fetch feeds
     both selects and the parent/child cascade. The rows are FLAT now — the
     new service sends categoryId / categoryName instead of a populated
     `category` document, so there is no nested object to read. */
  const categories = useMemo(() => {
    const byId = new Map();
    subCategories.forEach((s) => {
      const id = String(s?.categoryId || "");
      if (id && !byId.has(id)) byId.set(id, { id, name: s.categoryName });
    });
    return [...byId.values()].sort((a, b) =>
      String(a.name).localeCompare(String(b.name))
    );
  }, [subCategories]);

  const subsForCategory = useMemo(() => {
    const list = filter.category
      ? subCategories.filter(
          (s) => String(s?.categoryId || "") === filter.category
        )
      : subCategories;
    return [...list].sort((a, b) =>
      String(a.name).localeCompare(String(b.name))
    );
  }, [subCategories, filter.category]);

  const storeById = useMemo(() => {
    const m = new Map();
    stores.forEach((s) => m.set(String(s.id), s));
    return m;
  }, [stores]);

  const storeName = (id) => storeById.get(String(id))?.name || "a store";

  /* ── step 1: the identifier list currently in hand ── */
  const pasteLines = useMemo(
    () =>
      pasteText
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean),
    [pasteText]
  );

  const lines = tab === "paste" ? pasteLines : tab === "excel" ? excelLines : [];

  const filterActive =
    !!filter.category || !!filter.subCategory || !!filter.channel;

  const canResolve = tab === "filter" ? filterActive : lines.length > 0;

  /* any change upstream invalidates everything downstream */
  const resetFromStep1 = () => {
    setResolved(null);
    setChoices({});
    setResult(null);
    setError("");
  };

  const resetFromStep2 = () => {
    setResult(null);
    setError("");
  };

  /* ── Excel ──
     Read as a raw grid rather than objects. sheet_to_json's object mode always
     spends row 1 on column names, so a bare list with no header line loses its
     first product without saying a word. Working off the grid lets the header
     row be recognised on its merits and kept as data when it is not one. */
  const identifiersFromSheet = (sheet) => {
    const grid = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      blankrows: false,
      defval: "",
    });
    if (!grid.length) return [];

    /* first column carrying each recognised name; a repeated header keeps the
       leftmost, matching the way a reader would scan it */
    const columns = new Map();
    (Array.isArray(grid[0]) ? grid[0] : []).forEach((cell, i) => {
      const key = normKey(cell);
      if (key && !columns.has(key)) columns.set(key, i);
    });

    const hasHeader = ID_KEYS.some((k) => columns.has(k));
    /* Unrecognised headers stay in the data on purpose: a stray "Item Name"
       label surfaces once in Not found, where the operator can see and ignore
       it. Dropping it blind would take a real product with it. */
    const body = hasHeader ? grid.slice(1) : grid;

    const out = [];
    body.forEach((row) => {
      if (!Array.isArray(row)) return;

      let val = "";
      if (hasHeader) {
        /* per row, code wins over sku, which wins over name */
        for (let i = 0; i < ID_KEYS.length; i += 1) {
          const at = columns.get(ID_KEYS[i]);
          if (at === undefined) continue;
          const cell = String(row[at] ?? "").trim();
          if (cell) {
            val = cell;
            break;
          }
        }
      }
      /* no recognised header, or none of its columns filled on this row */
      if (!val) {
        const first = row.find((c) => String(c ?? "").trim());
        val = first === undefined ? "" : String(first).trim();
      }
      if (val) out.push(val);
    });

    /* the same code twice is one product, not two */
    return [...new Set(out)];
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onerror = () => {
      setError("Could not read that file — the browser could not open it.");
    };
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!firstSheet) {
          setError("That workbook has no sheets in it.");
          return;
        }
        const ids = identifiersFromSheet(firstSheet);
        resetFromStep1();
        setExcelLines(ids);
        setExcelFileName(file.name);
        if (ids.length === 0)
          setError(
            "No identifiers found in that sheet. Expected a column named code, sku or name."
          );
      } catch (err) {
        setError("Could not read that file — is it a valid .xlsx?");
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = "";
  };

  const downloadTemplate = () => {
    /* ONE IDENTIFIER PER ROW. Fill whichever column you have — the
       importer reads code first, then sku, then name. */
    const template = [
      { code: "300", sku: "", name: "" },
      { code: "701", sku: "", name: "" },
      { code: "", sku: "300-500grams", name: "" },
      { code: "", sku: "", name: "Sharbati Wheat Flour" },
    ];

    const readme = [
      { Instructions: "ONE PRODUCT PER ROW. Fill any ONE of the three columns — leave the others blank." },
      { Instructions: "code = the product code from Item Master (the most reliable match — every code is unique)." },
      { Instructions: "sku = a variant SKU code. It resolves to the product that variant belongs to." },
      { Instructions: "name = the product name. Use it only when you have no code; names can match more than one product." },
      { Instructions: "If a row fills more than one column, code wins, then sku, then name." },
      { Instructions: "Column headers are case-insensitive: 'Code', 'Product Code', 'SKU', 'Name' all work." },
      { Instructions: "The header row is optional. A sheet that is just one identifier per row, with no headings at all, is read as-is." },
      { Instructions: "Repeated identifiers are collapsed — listing a code twice allocates it once." },
      { Instructions: "This sheet chooses WHICH products to allocate. The stores are picked on screen in step 3." },
      { Instructions: "Nothing here changes stock or price — store availability only." },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    ws["!cols"] = [{ wch: 16 }, { wch: 20 }, { wch: 34 }];
    const wsReadme = XLSX.utils.json_to_sheet(readme);
    wsReadme["!cols"] = [{ wch: 120 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.utils.book_append_sheet(wb, wsReadme, "READ ME");
    XLSX.writeFile(wb, "milliyo_bulk_allocate_template.xlsx");
  };

  /* ── step 2: resolve ── */
  const matched = resolved?.matched || [];
  const ambiguous = resolved?.ambiguous || [];
  const notFound = resolved?.notFound || [];
  const counts = {
    matched: resolved?.counts?.matched ?? matched.length,
    ambiguous: resolved?.counts?.ambiguous ?? ambiguous.length,
    notFound: resolved?.counts?.notFound ?? notFound.length,
  };

  const runResolve = async () => {
    setResolving(true);
    setError("");
    setResult(null);
    try {
      const payload = {
        lines: tab === "filter" ? [] : lines,
        filter:
          tab === "filter"
            ? {
                ...(filter.category ? { category: filter.category } : {}),
                ...(filter.subCategory ? { subCategory: filter.subCategory } : {}),
                ...(filter.channel ? { channel: filter.channel } : {}),
              }
            : {},
      };
      // TODO(cutover): no server endpoint for this yet
      /* The catalogue matcher behind steps 1 and 2. Nothing in the .NET route
         list resolves a mixed list of product codes, variant SKUs and names to
         products, reports duplicates and near-misses, or filters by
         category + sub-category + channel — and nothing returns each match's
         current store scope, which is what the preview table renders.
         /admin/catalog/search/products is a search box, not this. Left on the
         old Node path and the old wrapper shape rather than dropped, so the
         feature fails loudly instead of disappearing. */
      const res = await api.post("/api/bulk/resolve", payload);
      const data = res.data?.data || {};
      setResolved(data);
      /* ambiguous rows start unresolved — the operator picks or skips */
      setChoices({});
    } catch (e) {
      setResolved(null);
      setError(apiError(e, "Could not match those items"));
    } finally {
      setResolving(false);
    }
  };

  /* every product the action will touch */
  const selectedIds = useMemo(() => {
    const ids = matched.map((m) => idOf(m)).filter(Boolean);
    ambiguous.forEach((_, i) => {
      if (choices[i]) ids.push(choices[i]);
    });
    return [...new Set(ids)];
  }, [matched, ambiguous, choices]);

  const selectedCount = selectedIds.length;

  /* productId -> readable row, for the failed-rows sheet */
  const productLabels = useMemo(() => {
    const m = new Map();
    matched.forEach((row) => {
      const id = idOf(row);
      if (id) m.set(id, { name: row.name || "", code: row.productCode || "" });
    });
    ambiguous.forEach((row) => {
      (row.candidates || []).forEach((c) => {
        const id = idOf(c);
        if (id) m.set(id, { name: c.name || "", code: c.productCode || "" });
      });
    });
    return m;
  }, [matched, ambiguous]);

  /* ── step 3 ── */
  const activeOp = OPERATIONS.find((o) => o.value === operation);
  const needsStores = !!activeOp?.needsStores;
  const canApply =
    !!activeOp && selectedCount > 0 && (!needsStores || storeIds.length > 0);

  const toggleStore = (id) => {
    resetFromStep2();
    setStoreIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* Ticking every store under "Hide in these stores" would leave a product on
     sale nowhere. The server refuses that outright, so the batch would come
     back entirely skipped — cheaper to say so before the round trip. */
  const cautionText = (() => {
    if (!activeOp) return "";
    if (
      operation === "hide_in" &&
      stores.length > 0 &&
      storeIds.length >= stores.length
    )
      return "That is every store you have. A product hidden in all of them would sell nowhere, so these will be skipped rather than applied.";
    return activeOp.caution || "";
  })();

  const sentence = () => {
    if (!activeOp) return "Pick an action to see what will happen.";
    if (selectedCount === 0) return "No products selected yet.";
    const noun = `${selectedCount} product${selectedCount === 1 ? "" : "s"}`;
    if (needsStores && storeIds.length === 0)
      return `Pick at least one store — ${noun} are waiting.`;
    const list = joinNames(storeIds.map(storeName));
    switch (operation) {
      case "all_stores":
        return `${noun} will be available in every store.`;
      case "limit_to":
        return `${noun} will be limited to ${list}.`;
      case "add_stores":
        return `${list} will be added to ${noun}. Any that already sell everywhere are left alone unless a previous hide took them out of ${list}.`;
      case "remove_stores":
        return `${list} will be removed from ${noun}.`;
      case "hide_in":
        return `${noun} will be hidden in ${list}.`;
      case "unhide_in":
        return `${noun} will be visible again in ${list}.`;
      default:
        return "";
    }
  };

  /* ── step 4: apply ── */
  const runApply = async () => {
    setConfirming(false);
    setApplying(true);
    setError("");
    try {
      // TODO(cutover): no server endpoint for this yet
      /* The batch write behind step 4: six verbs (all_stores / limit_to /
         add_stores / remove_stores / hide_in / unhide_in) over many products,
         answering with skipped / errors / converted. There is no
         /admin/bulk/allocate. PUT /admin/catalog/products/{id}/stores is the
         nearest thing and is not a mapping for it: one product per call, an
         absolute scope rather than a verb, and no per-row reporting — the
         batch semantics would have to be rebuilt on top of it. Left on the old
         Node path and the old wrapper shape rather than dropped. */
      const res = await api.post("/api/bulk/allocate", {
        productIds: selectedIds,
        operation,
        storeIds: needsStores ? storeIds : [],
      });
      setResult(res.data?.data || {});
    } catch (e) {
      const payload = e.response?.data;
      /* "No product could be updated" comes back as a 500 that still carries
         its per-row detail. Keep it, or the failed-rows sheet the operator is
         told to re-upload would download empty. */
      const detail = payload?.data;
      if (
        detail &&
        ((detail.errors || []).length || (detail.skipped || []).length)
      ) {
        setResult(detail);
      }
      setError(apiError(e, "The allocation failed"));
    } finally {
      setApplying(false);
    }
  };

  /* everything the operator has to fix and re-upload */
  const failedRows = useMemo(() => {
    const rows = [];

    notFound.forEach((x) => {
      rows.push({
        code: lineOf(x),
        sku: "",
        name: "",
        reason: "No product matched this identifier",
      });
    });

    ambiguous.forEach((row, i) => {
      if (choices[i]) return;
      const n = (row.candidates || []).length;
      rows.push({
        code: lineOf(row),
        sku: "",
        name: "",
        /* a guess row matched nothing at all — saying it "matched N products"
           would send the operator looking for duplicates that do not exist */
        reason:
          row.exact === false
            ? `No exact match — ${n} near-miss suggestion(s) offered, none picked. Use the product code.`
            : `Matched ${n} products — skipped. Use the product code instead.`,
      });
    });

    (result?.skipped || []).forEach((s) => {
      const label = productLabels.get(String(s?.productId)) || {};
      rows.push({
        code: label.code || "",
        sku: "",
        name: s?.name || label.name || String(s?.productId || ""),
        reason: s?.reason || "Skipped",
      });
    });

    (result?.errors || []).forEach((e) => {
      const id = String(e?.productId || "");
      const label = productLabels.get(id) || {};
      rows.push({
        code: label.code || "",
        sku: "",
        name: label.name || id,
        reason: typeof e === "string" ? e : e?.reason || e?.message || "Failed",
      });
    });

    return rows;
  }, [notFound, ambiguous, choices, result, productLabels]);

  const downloadFailed = () => {
    if (failedRows.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(failedRows);
    ws["!cols"] = [{ wch: 16 }, { wch: 20 }, { wch: 34 }, { wch: 60 }];
    const readme = [
      { Instructions: "These rows were NOT allocated. Fix the identifier and re-upload this sheet on the Excel tab." },
      { Instructions: "The 'reason' column is ignored on re-upload — leave it or delete it." },
      { Instructions: "For rows that matched several products, replace the value with the exact product code." },
    ];
    const wsReadme = XLSX.utils.json_to_sheet(readme);
    wsReadme["!cols"] = [{ wch: 120 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.utils.book_append_sheet(wb, wsReadme, "READ ME");
    XLSX.writeFile(wb, "milliyo_bulk_allocate_failed.xlsx");
  };

  const startOver = () => {
    setPasteText("");
    setExcelLines([]);
    setExcelFileName("");
    setFilter({ category: "", subCategory: "", channel: "" });
    setResolved(null);
    setChoices({});
    setOperation("");
    setStoreIds([]);
    setResult(null);
    setError("");
  };

  /* ── which step is live ── */
  const currentStep = result ? 4 : activeOp ? 3 : resolved ? 2 : 1;
  const stepReached = (n) => {
    if (n === 1) return true;
    if (n === 2) return !!resolved;
    if (n === 3) return !!resolved;
    if (n === 4) return !!result;
    return false;
  };

  /* ── small pieces ── */
  const countTile = (label, value, color) => (
    <div
      style={{
        flex: 1,
        minWidth: 150,
        border: "1.5px solid var(--mly-border)",
        borderRadius: 12,
        background: "var(--mly-surface-2)",
        padding: "14px 16px",
      }}
    >
      <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1.1 }}>
        {value}
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: "uppercase",
          color: "var(--mly-text-muted)",
          marginTop: 4,
        }}
      >
        {label}
      </div>
    </div>
  );

  const scopeText = (scope) => {
    if (!scope) return "-";
    if (typeof scope === "string") return scope;
    const all = scope.availableInAllStores !== false;
    const list = (scope.stores || []).map((s) => storeName(s?._id || s));
    const hidden = (scope.excludedStores || []).map((s) => storeName(s?._id || s));
    if (all) {
      return hidden.length
        ? `All stores, except ${joinNames(hidden)}`
        : "All stores";
    }
    return list.length ? `Only ${joinNames(list)}` : "No stores";
  };

  const checkbox = (checked, color) => (
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: 5,
        border: `2px solid ${checked ? color : "var(--mly-border)"}`,
        background: checked ? color : "var(--mly-surface)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "all 0.15s",
      }}
    >
      {checked && (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
          <polyline
            points="2,6 5,9 10,3"
            stroke="var(--mly-on-accent)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );

  const stepHead = (n, title, sub) => (
    <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 800,
          background: "var(--mly-accent-soft)",
          color: "var(--mly-heading)",
          border: "1px solid var(--mly-border)",
        }}
      >
        {n}
      </div>
      <div>
        <h6
          style={{
            margin: 0,
            fontWeight: 700,
            fontSize: 15,
            color: "var(--mly-heading)",
          }}
        >
          {title}
        </h6>
        {sub && (
          <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
            {sub}
          </span>
        )}
      </div>
    </div>
  );

  const tabButton = (key, label) => (
    <div
      key={key}
      onClick={() => {
        setTab(key);
        resetFromStep1();
      }}
      style={{
        padding: "9px 18px",
        borderRadius: 10,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 700,
        userSelect: "none",
        border:
          tab === key
            ? "2px solid var(--mly-heading)"
            : "1.5px solid var(--mly-border)",
        background:
          tab === key ? "var(--mly-accent-soft)" : "var(--mly-surface-2)",
        color: tab === key ? "var(--mly-heading)" : "var(--mly-text-muted)",
        transition: "all 0.15s",
      }}
    >
      {label}
    </div>
  );

  /* A scoped admin reaching this URL would get a 403 out of both calls and a
     bare "Super admin access required" banner. Say so up front instead. */
  if (!canAllocate) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <h5 style={{ margin: "0 0 8px", fontWeight: 700 }}>Bulk Allocate</h5>
            <div style={{ fontSize: 13.5, color: "var(--mly-text-muted)" }}>
              Allocating the catalogue decides what every store sells, so it is
              limited to a super admin. Ask one to run the batch for you.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mly-bulk">
      <style>{BULK_CSS}</style>
      <div className="container-fluid">
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <div>
              <h5 style={{ margin: 0, fontWeight: 700 }}>Bulk Allocate</h5>
              <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
                Put many products into stores in one pass. Store availability
                only — stock and price are untouched.
              </span>
            </div>
            <button className="btn btn-primary btn-sm widthbtn" onClick={startOver}>
              Start over
            </button>
          </div>

          <div className="card-body">
            {/* ── step rail ── */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 20,
              }}
            >
              {STEPS.map((s) => {
                const done = stepReached(s.n) && currentStep > s.n;
                const live = currentStep === s.n && stepReached(s.n);
                return (
                  <div
                    key={s.n}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "7px 14px",
                      borderRadius: 20,
                      fontSize: 12.5,
                      fontWeight: 700,
                      border:
                        live || done
                          ? "1.5px solid var(--mly-heading)"
                          : "1.5px solid var(--mly-border)",
                      background: live
                        ? "var(--mly-accent-soft)"
                        : "var(--mly-surface-2)",
                      color:
                        live || done
                          ? "var(--mly-heading)"
                          : "var(--mly-text-muted)",
                    }}
                  >
                    <span>{done ? "✓" : s.n}</span>
                    <span>{s.title}</span>
                  </div>
                );
              })}
            </div>

            {refError && (
              <div
                style={{
                  background: "var(--bulk-danger-soft)",
                  color: "var(--bulk-danger)",
                  padding: "10px 14px",
                  borderRadius: 8,
                  marginBottom: 16,
                  fontSize: 13,
                }}
              >
                {refError}
              </div>
            )}

            {error && (
              <div
                style={{
                  background: "var(--bulk-danger-soft)",
                  color: "var(--bulk-danger)",
                  padding: "10px 14px",
                  borderRadius: 8,
                  marginBottom: 16,
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}

            {/* ══ STEP 1 — SELECT ITEMS ══ */}
            <div style={sectionStyle}>
              {stepHead(
                1,
                "Select items",
                "Paste a list, upload a sheet, or filter the catalogue."
              )}

              <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                {tabButton("paste", "Paste")}
                {tabButton("excel", "Excel")}
                {tabButton("filter", "Filter")}
              </div>

              {tab === "paste" && (
                <div>
                  <label style={labelStyle}>
                    One identifier per line
                    <span
                      style={{
                        fontWeight: 500,
                        letterSpacing: 0,
                        textTransform: "none",
                        color: "var(--mly-text-muted)",
                        marginLeft: 6,
                      }}
                    >
                      (product code, variant SKU or name — mix them freely)
                    </span>
                  </label>
                  <textarea
                    value={pasteText}
                    onChange={(e) => {
                      setPasteText(e.target.value);
                      resetFromStep1();
                    }}
                    rows={9}
                    placeholder={"300\n701\n300-500grams\nSharbati Wheat Flour"}
                    style={{
                      ...inputStyle,
                      fontFamily: "var(--mly-font-mono, monospace)",
                      fontSize: 13,
                      resize: "vertical",
                    }}
                  />
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--mly-text-muted)",
                      marginTop: 6,
                    }}
                  >
                    {pasteLines.length} line{pasteLines.length === 1 ? "" : "s"} ready
                  </div>
                </div>
              )}

              {tab === "excel" && (
                <div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <div
                      onClick={() => fileRef.current && fileRef.current.click()}
                      style={{
                        flex: 1,
                        minWidth: 260,
                        border: "2px dashed var(--mly-heading)",
                        borderRadius: 12,
                        background: excelLines.length
                          ? "var(--mly-surface)"
                          : "var(--mly-accent-soft)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 110,
                        cursor: "pointer",
                        padding: 12,
                        textAlign: "center",
                      }}
                    >
                      <div style={{ fontSize: 22 }}>📄</div>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "var(--mly-text-soft)",
                        }}
                      >
                        {excelFileName || "UPLOAD A SHEET"}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--mly-text-muted)" }}>
                        {excelLines.length
                          ? `${excelLines.length} identifier${
                              excelLines.length === 1 ? "" : "s"
                            } read`
                          : "XLSX, XLS or CSV — a code, sku or name column, or just one identifier per row"}
                      </span>
                      <input
                        ref={fileRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileUpload}
                        style={{ display: "none" }}
                      />
                    </div>

                    <div
                      style={{
                        minWidth: 220,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      <button onClick={downloadTemplate} style={ghostBtn}>
                        Download template
                      </button>
                      <span style={{ fontSize: 11.5, color: "var(--mly-text-muted)" }}>
                        The template carries a READ ME tab explaining every column.
                      </span>
                    </div>
                  </div>

                  {excelLines.length > 0 && (
                    <div
                      style={{
                        marginTop: 12,
                        border: "1.5px solid var(--mly-border)",
                        borderRadius: 10,
                        background: "var(--mly-surface-2)",
                        padding: "10px 14px",
                        fontSize: 12.5,
                        color: "var(--mly-text-soft)",
                        fontFamily: "var(--mly-font-mono, monospace)",
                      }}
                    >
                      {excelLines.slice(0, 12).join(", ")}
                      {excelLines.length > 12
                        ? ` … +${excelLines.length - 12} more`
                        : ""}
                    </div>
                  )}
                </div>
              )}

              {tab === "filter" && (
                <div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <label style={labelStyle}>Category</label>
                      <select
                        value={filter.category}
                        onChange={(e) => {
                          setFilter({
                            ...filter,
                            category: e.target.value,
                            subCategory: "",
                          });
                          resetFromStep1();
                        }}
                        style={inputStyle}
                      >
                        <option value="">All categories</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ flex: 1, minWidth: 200 }}>
                      <label style={labelStyle}>Sub Category</label>
                      <select
                        value={filter.subCategory}
                        onChange={(e) => {
                          setFilter({ ...filter, subCategory: e.target.value });
                          resetFromStep1();
                        }}
                        style={inputStyle}
                      >
                        <option value="">All sub categories</option>
                        {subsForCategory.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ flex: 1, minWidth: 200 }}>
                      <label style={labelStyle}>Channel</label>
                      <select
                        value={filter.channel}
                        onChange={(e) => {
                          setFilter({ ...filter, channel: e.target.value });
                          resetFromStep1();
                        }}
                        style={inputStyle}
                      >
                        <option value="">Both channels</option>
                        <option value="prepacked">Prepacked packs</option>
                        <option value="bulkMilling">Bulk milling</option>
                      </select>
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--mly-text-muted)",
                      marginTop: 10,
                    }}
                  >
                    {filterActive
                      ? "Selecting all matching only lists them — nothing is written yet."
                      : "Choose at least one filter."}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 18 }}>
                <button
                  onClick={runResolve}
                  disabled={!canResolve || resolving}
                  style={primaryBtn(!canResolve || resolving)}
                >
                  {resolving
                    ? "Matching…"
                    : tab === "filter"
                    ? "Select all matching"
                    : "Preview matches"}
                </button>
              </div>
            </div>

            {/* ══ STEP 2 — PREVIEW ══ */}
            {resolved && (
              <div style={sectionStyle}>
                {stepHead(
                  2,
                  "Preview",
                  "What the catalogue matched. Nothing has been written yet."
                )}

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                    marginBottom: 18,
                  }}
                >
                  {countTile("Matched", counts.matched, "var(--mly-heading)")}
                  {countTile("Need a choice", counts.ambiguous, "var(--bulk-warn)")}
                  {countTile("Not found", counts.notFound, "var(--bulk-danger)")}
                  {countTile("Will be changed", selectedCount, "var(--mly-accent)")}
                </div>

                {/* matched */}
                {matched.length > 0 && (
                  <div style={{ marginBottom: 18 }}>
                    <label style={labelStyle}>Matched products</label>
                    <div style={{ overflowX: "auto", maxHeight: 340, overflowY: "auto" }}>
                      <table className="display table table-bordered">
                        <thead>
                          <tr>
                            <th style={{ minWidth: 50 }}>#</th>
                            <th style={{ minWidth: 130 }}>You typed</th>
                            <th style={{ minWidth: 220 }}>Product</th>
                            <th style={{ minWidth: 110 }}>Code</th>
                            <th style={{ minWidth: 200 }}>Sells in today</th>
                          </tr>
                        </thead>
                        <tbody>
                          {matched.map((row, i) => (
                            <tr key={`${idOf(row)}-${i}`}>
                              <td>{i + 1}</td>
                              <td
                                style={{
                                  fontFamily: "var(--mly-font-mono, monospace)",
                                  fontSize: 12.5,
                                }}
                              >
                                {lineOf(row) || "—"}
                              </td>
                              <td>{row.name || "—"}</td>
                              <td>{row.productCode || "—"}</td>
                              <td style={{ fontSize: 12.5, color: "var(--mly-text-muted)" }}>
                                {scopeText(row.currentScope)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ambiguous */}
                {ambiguous.length > 0 && (
                  <div style={{ marginBottom: 18 }}>
                    <label style={labelStyle}>
                      Need a choice — nothing was picked for you
                    </label>
                    <div
                      style={{
                        border: "1.5px solid var(--mly-border)",
                        borderRadius: 12,
                        background: "var(--mly-surface-2)",
                        padding: "8px 14px",
                        maxHeight: 300,
                        overflowY: "auto",
                      }}
                    >
                      {ambiguous.map((row, i) => (
                        <div
                          key={`amb-${i}`}
                          style={{
                            display: "flex",
                            gap: 12,
                            alignItems: "center",
                            flexWrap: "wrap",
                            padding: "10px 0",
                            borderBottom:
                              i === ambiguous.length - 1
                                ? "none"
                                : "1px solid var(--mly-border-soft)",
                          }}
                        >
                          <div
                            style={{
                              minWidth: 190,
                              display: "flex",
                              flexDirection: "column",
                              gap: 3,
                            }}
                          >
                            <span
                              style={{
                                fontFamily: "var(--mly-font-mono, monospace)",
                                fontSize: 12.5,
                                color: "var(--mly-text-soft)",
                              }}
                            >
                              {lineOf(row) || "—"}
                            </span>
                            {/* an exact duplicate and a spelling guess look the
                                same in a dropdown; say which this is, so a
                                near-miss is never mistaken for a real match */}
                            <span
                              style={{
                                fontSize: 10.5,
                                fontWeight: 700,
                                letterSpacing: 0.4,
                                color:
                                  row.exact === false
                                    ? "var(--bulk-danger)"
                                    : "var(--bulk-warn)",
                              }}
                            >
                              {row.exact === false
                                ? "NO EXACT MATCH — GUESSES"
                                : "DUPLICATE"}
                            </span>
                            {row.reason && (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "var(--mly-text-muted)",
                                  lineHeight: 1.35,
                                }}
                              >
                                {row.reason}
                              </span>
                            )}
                          </div>
                          <select
                            value={choices[i] || ""}
                            onChange={(e) => {
                              setChoices((prev) => ({
                                ...prev,
                                [i]: e.target.value,
                              }));
                              resetFromStep2();
                            }}
                            style={{ ...inputStyle, flex: 1, minWidth: 260 }}
                          >
                            <option value="">Skip this one</option>
                            {(row.candidates || []).map((c, ci) => (
                              <option key={`${idOf(c)}-${ci}`} value={idOf(c)}>
                                {c.name || "Unnamed"}
                                {c.productCode ? ` · ${c.productCode}` : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* not found */}
                {notFound.length > 0 && (
                  <div style={{ marginBottom: 18 }}>
                    <label style={labelStyle}>Nothing matched these</label>
                    <div
                      style={{
                        border: "1.5px solid var(--mly-border)",
                        borderRadius: 12,
                        background: "var(--bulk-danger-soft)",
                        padding: "10px 14px",
                        fontSize: 12.5,
                        color: "var(--mly-text-soft)",
                        fontFamily: "var(--mly-font-mono, monospace)",
                        maxHeight: 160,
                        overflowY: "auto",
                      }}
                    >
                      {notFound.map(lineOf).join(", ")}
                    </div>
                    <button
                      onClick={downloadFailed}
                      style={{ ...linkBtn, marginTop: 8 }}
                    >
                      Download unmatched rows
                    </button>
                  </div>
                )}

                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "var(--mly-text-muted)",
                    background: "var(--mly-surface-2)",
                    border: "1.5px solid var(--mly-border)",
                    borderRadius: 10,
                    padding: "10px 14px",
                  }}
                >
                  This is a preview. Nothing is saved until you press Apply in
                  step 4.
                </div>
              </div>
            )}

            {/* ══ STEP 3 — CHOOSE THE ACTION ══ */}
            {resolved && (
              <div style={sectionStyle}>
                {stepHead(
                  3,
                  "Choose the action",
                  `${selectedCount} product${
                    selectedCount === 1 ? "" : "s"
                  } will be affected.`
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                    gap: 10,
                    marginBottom: 18,
                  }}
                >
                  {OPERATIONS.map((op) => {
                    const sel = operation === op.value;
                    return (
                      <div
                        key={op.value}
                        onClick={() => {
                          setOperation(op.value);
                          resetFromStep2();
                        }}
                        style={{
                          padding: "12px 14px",
                          borderRadius: 12,
                          cursor: "pointer",
                          userSelect: "none",
                          border: sel
                            ? "2px solid var(--mly-heading)"
                            : "1.5px solid var(--mly-border)",
                          background: sel
                            ? "var(--mly-accent-soft)"
                            : "var(--mly-surface-2)",
                          transition: "all 0.15s",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: sel
                              ? "var(--mly-heading)"
                              : "var(--mly-text-soft)",
                          }}
                        >
                          {op.title}
                        </div>
                        <div
                          style={{
                            fontSize: 11.5,
                            color: "var(--mly-text-muted)",
                            marginTop: 4,
                            lineHeight: 1.45,
                          }}
                        >
                          {op.hint}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {needsStores && (
                  <div style={{ marginBottom: 18 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 8,
                      }}
                    >
                      <label style={{ ...labelStyle, marginBottom: 0 }}>
                        Stores
                      </label>
                      <button
                        onClick={() => {
                          resetFromStep2();
                          setStoreIds(
                            storeIds.length === stores.length
                              ? []
                              : stores.map((s) => String(s.id))
                          );
                        }}
                        style={linkBtn}
                      >
                        {storeIds.length === stores.length && stores.length > 0
                          ? "Clear all"
                          : "Select all"}
                      </button>
                    </div>

                    <div
                      style={{
                        border: "1.5px solid var(--mly-border)",
                        borderRadius: 10,
                        background: "var(--mly-surface)",
                        padding: "8px 12px",
                        maxHeight: 220,
                        overflowY: "auto",
                      }}
                    >
                      {stores.length === 0 ? (
                        <div
                          style={{
                            fontSize: 12.5,
                            color: "var(--mly-text-muted)",
                            padding: "6px 2px",
                          }}
                        >
                          No stores yet — add one on the Stores page first.
                        </div>
                      ) : (
                        stores.map((s) => {
                          const id = String(s.id);
                          const checked = storeIds.includes(id);
                          return (
                            <div
                              key={id}
                              onClick={() => toggleStore(id)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "6px 2px",
                                cursor: "pointer",
                                userSelect: "none",
                              }}
                            >
                              {checkbox(checked, "var(--mly-heading)")}
                              <span
                                style={{
                                  fontSize: 13,
                                  color: "var(--mly-text-soft)",
                                  fontWeight: checked ? 600 : 400,
                                }}
                              >
                                {s.name}
                                {s.code ? (
                                  <span
                                    style={{
                                      color: "var(--mly-text-muted)",
                                      fontWeight: 400,
                                    }}
                                  >
                                    {" "}
                                    · {s.code}
                                  </span>
                                ) : null}
                                {s.isDefault ? (
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      background: "var(--mly-accent-soft)",
                                      color: "var(--mly-heading)",
                                      fontSize: 9,
                                      fontWeight: 700,
                                      padding: "1px 7px",
                                      borderRadius: 20,
                                      border: "1px solid var(--mly-border)",
                                    }}
                                  >
                                    MAIN
                                  </span>
                                ) : null}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                <div
                  style={{
                    border: "1.5px solid var(--mly-heading)",
                    borderRadius: 12,
                    background: "var(--mly-accent-soft)",
                    padding: "14px 16px",
                    fontSize: 14.5,
                    fontWeight: 700,
                    color: "var(--mly-heading)",
                  }}
                >
                  {sentence()}
                </div>

                {cautionText && (
                  <div
                    style={{
                      marginTop: 10,
                      border: "1.5px solid var(--bulk-warn)",
                      borderRadius: 12,
                      background: "var(--bulk-warn-soft)",
                      padding: "12px 16px",
                      fontSize: 13,
                      fontWeight: 600,
                      lineHeight: 1.5,
                      color: "var(--bulk-warn)",
                    }}
                  >
                    {cautionText}
                  </div>
                )}
              </div>
            )}

            {/* ══ STEP 4 — APPLY ══ */}
            {resolved && (
              <div style={sectionStyle}>
                {stepHead(
                  4,
                  "Apply",
                  "This writes to the catalogue. Store availability only."
                )}

                {!result ? (
                  <button
                    onClick={() => setConfirming(true)}
                    disabled={!canApply || applying}
                    style={primaryBtn(!canApply || applying)}
                  >
                    {applying
                      ? "Applying…"
                      : `Apply to ${selectedCount} product${
                          selectedCount === 1 ? "" : "s"
                        }`}
                  </button>
                ) : (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        gap: 12,
                        flexWrap: "wrap",
                        marginBottom: 16,
                      }}
                    >
                      {countTile(
                        "Updated",
                        result.updated ?? 0,
                        "var(--mly-heading)"
                      )}
                      {countTile(
                        "Skipped",
                        (result.skipped || []).length,
                        "var(--bulk-warn)"
                      )}
                      {countTile("Errors", (result.errors || []).length, "var(--bulk-danger)")}
                    </div>

                    {/* "Limit to stores" turns a sells-everywhere product into
                        a whitelist of exactly the picked stores. That succeeds,
                        so it is not an error — but it is the change with the
                        widest blast radius, and burying it in the Updated count
                        is how a catalogue quietly disappears from every branch
                        but one. The server names each product it happened to. */}
                    {(result.converted || []).length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>
                          Narrowed from all stores ({result.converted.length})
                        </label>
                        <div
                          style={{
                            border: "1.5px solid var(--bulk-warn)",
                            borderRadius: 10,
                            background: "var(--bulk-warn-soft)",
                            padding: "10px 14px",
                            maxHeight: 200,
                            overflowY: "auto",
                            fontSize: 12.5,
                            color: "var(--mly-text-soft)",
                          }}
                        >
                          {result.converted.map((c, i) => (
                            <div key={`conv-${i}`} style={{ padding: "3px 0" }}>
                              {c?.reason ||
                                `${
                                  c?.name ||
                                  productLabels.get(String(c?.productId))?.name ||
                                  String(c?.productId || "A product")
                                } now sells in the selected stores only`}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(result.skipped || []).length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>Skipped</label>
                        <div
                          style={{
                            border: "1.5px solid var(--mly-border)",
                            borderRadius: 10,
                            background: "var(--mly-surface-2)",
                            padding: "10px 14px",
                            maxHeight: 200,
                            overflowY: "auto",
                            fontSize: 12.5,
                            color: "var(--mly-text-soft)",
                          }}
                        >
                          {(result.skipped || []).map((s, i) => {
                            const label =
                              productLabels.get(String(s?.productId)) || {};
                            return (
                              <div key={`skip-${i}`} style={{ padding: "3px 0" }}>
                                {s?.name || label.name || String(s?.productId || "—")}
                                <span style={{ color: "var(--mly-text-muted)" }}>
                                  {" "}
                                  — {s?.reason || "skipped"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {(result.errors || []).length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>Errors</label>
                        <div
                          style={{
                            border: "1.5px solid var(--mly-border)",
                            borderRadius: 10,
                            background: "var(--bulk-danger-soft)",
                            padding: "10px 14px",
                            maxHeight: 200,
                            overflowY: "auto",
                            fontSize: 12.5,
                            color: "var(--mly-text-soft)",
                          }}
                        >
                          {(result.errors || []).map((e, i) => (
                            <div key={`err-${i}`} style={{ padding: "3px 0" }}>
                              {typeof e === "string"
                                ? e
                                : e?.reason || e?.message || "Failed"}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      {failedRows.length > 0 && (
                        <button onClick={downloadFailed} style={ghostBtn}>
                          Download failed rows ({failedRows.length})
                        </button>
                      )}
                      <button onClick={startOver} style={primaryBtn(false)}>
                        Allocate another batch
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── confirm ── */}
      {confirming && (
        <>
          <div
            onClick={() => setConfirming(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(2px)",
              zIndex: 1040,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1050,
              width: "100%",
              maxWidth: 460,
              background: "var(--mly-surface)",
              borderRadius: 20,
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              padding: "28px 32px",
            }}
          >
            <h2 style={{ margin: "0 0 14px", fontSize: 20, fontWeight: 700 }}>
              Apply this change?
            </h2>
            <div
              style={{
                fontSize: 14,
                color: "var(--mly-text-soft)",
                lineHeight: 1.6,
                marginBottom: cautionText ? 12 : 20,
              }}
            >
              {sentence()}
            </div>
            {cautionText && (
              <div
                style={{
                  border: "1.5px solid var(--bulk-warn)",
                  borderRadius: 10,
                  background: "var(--bulk-warn-soft)",
                  padding: "10px 14px",
                  fontSize: 12.5,
                  fontWeight: 600,
                  lineHeight: 1.5,
                  color: "var(--bulk-warn)",
                  marginBottom: 20,
                }}
              >
                {cautionText}
              </div>
            )}
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setConfirming(false)} style={ghostBtn}>
                Cancel
              </button>
              <button
                onClick={runApply}
                style={{ ...primaryBtn(false), flex: 1 }}
              >
                Yes, apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BulkAllocate;
